/* import __COLOCATED_TEMPLATE__ from './lsg-send-message.hbs'; */
import { scheduleOnce } from '@ember/runloop';
import { computed } from '@ember/object';
import { not } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import LSGModalBase from './lsg-modal-dialog-base';
import { showModal, closeModal } from "sh-www/utils/modal-controls";
import AssetUploadManager from '../../../mixins/asset-upload-manager';

export default LSGModalBase.extend(AssetUploadManager, {
  /**
   * Register services
   */
  applicationStateManager: service('state-managers/application'),
  threadCommandService : service('commands/thread-commands'),

  /**
   * @inheritDoc
   */
  propertyDefaults: {
    /**
     * @type {Thread} The thread to add the message to. If falsey, a new thread will be created.
     */
    thread: null,

    /**
     * @type {User|null}    The user we should send the message to
     */
    userToSendMessageTo: null,
  },

  /**
   * @inheritDoc
   */
  stateDefaults: {
    /**
     * @type {String}   The message the user has input
     */
    message: '',

    /**
     * @type {String}   The subject of the message.
     */
    messageSubject: '',

    /**
     * Show the saved message selector
     *
     * @type {Boolean}
     */
    showSavedMessages: false,
  },

  /**
   * @inheritDoc
   */
  _onClose() {
    this.resetAssetUploadManager();
  },

  /**
   * @type {Boolean} Whether the user needs to enter a subject. Only if there is no existing thread.
   */
  _hasSubjectField: not('model.thread'),

  /**
   * @type {Boolean} Whether the save button is disabled.
   */
  _isSaveButtonDisabled: computed(
    'isUploadingAssets',
    'state.message',
    'state.messageSubject',
    '_hasSubjectField',
    function () {
      return this.get('isUploadingAssets')
        || (this.get('state.messageSubject') === '' && this.get('_hasSubjectField'))
        || this.get('state.message') === '';
    },
  ),

  _onInitializationComplete() {
    this._super(...arguments);

    this.set('state.message', this.get('model.message') || '');
    this.set('state.messageSubject', this.get('model.messageSubject') || '');
  },

  actions: {
    /**
     * Called when the file input component receives new files.
     */
    onFileInputChange: function(files) {
      const message = this.get('state.message');
      const messageSubject = this.get('state.messageSubject');
      const userToSendMessageTo = this.get('model.userToSendMessageTo');
      const thread = this.get('model.thread');

      this.addFiles(files, {
        onCloseEnd: () => {
          showModal(this, 'lsg/lsg-send-message', {
            message,
            messageSubject,
            userToSendMessageTo,
            thread,
          });
        },
      });
    },

    /**
     * Called when an asset's delete button is clicked.
     */
    onClickDeleteAssetButton: function(uploadID) {
      this.removeFile(uploadID);
    },

    onInputMessageSubject(text) {
      this.set('state.messageSubject', text);
    },

    onInputMessage(text) {
      this.set('state.message', text);
    },

    /**
     * Called when the user confirms the send.
     */
    onSendConfirmed: function() {
      const thread = this.get('model.thread');
      const toUser = this.get('model.userToSendMessageTo');
      const subject = this.get('state.messageSubject');
      const message = this.get('state.message');

      const assetIDs = [];

      this.get('uploadCompletedList').forEach((uploadID) => {
        const assetID = this.get(`uploads.${uploadID}.assetID`);
        assetIDs.push(assetID);
      });

      const includes = [
        'user',
        'assets.uri-collection',
      ];

      const onSuccess = () => {
        this.get('applicationStateManager').addToast('Message sent.');
        closeModal(this);
      }
      const onFailure = () => this.get('applicationStateManager').addToast('Message failed to send.');

      if (this.model.sendMessage) {
        this.model.sendMessage(thread, toUser, message, assetIDs, subject);
        closeModal(this);
        return;
      }

      // If a thread was supplied, post to that thread.
      if (thread) {
        this.get('threadCommandService')
          .addNewMessageToThread(thread, message, assetIDs, includes)
          .then(onSuccess)
          .catch(onFailure);

        return;
      }

      // Otherwise, create a new thread.
      this.get('threadCommandService')
        .sendNewMessage(toUser, subject, message, assetIDs)
        .then(onSuccess)
        .catch(onFailure);
    },

    /**
     * Called when the user selects a saved message
     *
     * @param message
     */
    onMessageSelect(message) {
      const currentValue = this.get('state.message') || '';
      const newValue = currentValue + (currentValue ? '\n' : '') + message.get('body');

      this.setProperties({
        'state.showSavedMessages': false,
        'state.message': newValue,
      });

      scheduleOnce('afterRender', this, () => {
        document.querySelector('.SH-c-lsg-send-message textarea').focus();
      });
    },

    /**
     * Called when the user selects the saved message call-to-action
     */
    showSavedMessagesModal() {
      this.set('state.showSavedMessages', true);
    },

    /**
     * Called when the user closes the saved messages modal
     */
    closeSavedMessagesModal() {
      this.set('state.showSavedMessages', false);
    },

    closeModal() {
      closeModal(this);
    },
  }
});
